import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { AdminUser } from '@utilities/types';

interface IAuthContext {
    admin: AdminUser | null;
    token: string | null;
    isAuthenticated: boolean;
    login: (token: string, admin: AdminUser) => void;
    logout: () => void;
    updateAdmin: (admin: AdminUser) => void;
}

const AuthContext = createContext<IAuthContext>({
    admin: null,
    token: null,
    isAuthenticated: false,
    login: () => { },
    logout: () => { },
    updateAdmin: () => { },
});

interface IAuthContextProvider {
    children: ReactNode;
}

export const AuthContextProvider: FC<IAuthContextProvider> = ({ children }) => {
    const [admin, setAdmin] = useState<AdminUser | null>(() => {
        try {
            const saved = localStorage.getItem('admin');
            return saved ? JSON.parse(saved) : null;
        } catch {
            localStorage.removeItem('admin');
            return null;
        }
    });

    const [token, setToken] = useState<string | null>(() => {
        return localStorage.getItem('token');
    });

    const isAuthenticated = !!token && !!admin;

    const login = useCallback((newToken: string, newAdmin: AdminUser) => {
        localStorage.setItem('token', newToken);
        localStorage.setItem('admin', JSON.stringify(newAdmin));
        setToken(newToken);
        setAdmin(newAdmin);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('admin');
        setToken(null);
        setAdmin(null);
    }, []);

    const updateAdmin = useCallback((updatedAdmin: AdminUser) => {
        localStorage.setItem('admin', JSON.stringify(updatedAdmin));
        setAdmin(updatedAdmin);
    }, []);

    useEffect(() => {
        // Vérifier si le token est encore valide au chargement
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (payload.exp * 1000 < Date.now()) {
                    logout();
                }
            } catch {
                logout();
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AuthContext.Provider value={{ admin, token, isAuthenticated, login, logout, updateAdmin }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
